import { useFirestoreDoc } from "@p0security/frontend-core/react";
import { AwsIntegration } from "@p0security/integrations-aws-shared/types";
import { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import {
  AzureAdIntegration,
  EntraIdIntegration,
  isLegacyAzureAdIntegration,
} from "@p0security/integrations-entra-id-shared";
import { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import { OktaIntegration } from "@p0security/integrations-okta-shared/types";
import { installedItems } from "@p0security/shared/install/installed";
import { IntegrationStatus } from "@p0security/shared/integrations/shared";
import { AssessmentScopeIntegration } from "@p0security/shared/types/assessment";
import { useFlags } from "launchdarkly-react-client-sdk";
import { size } from "lodash";
import { useMemo } from "react";

export const useInstalledIntegrations = () => {
  const flags = useFlags();
  const { entraAssessment, oktaAssessment, customAssessment, azureAssessment } =
    flags;
  const { doc: gcloudDoc, loading: gcloudLoading } =
    useFirestoreDoc<GcloudIntegration>(`integrations/gcloud`, {
      live: true,
      tenantAware: true,
    });

  const { doc: awsDoc, loading: awsLoading } = useFirestoreDoc<AwsIntegration>(
    `integrations/aws`,
    {
      live: true,
      tenantAware: true,
    }
  );

  const { doc: oktaDoc, loading: oktaLoading } =
    useFirestoreDoc<OktaIntegration>(`integrations/okta`, {
      live: true,
      tenantAware: true,
    });

  const { doc: workspaceDoc, loading: workspaceLoading } = useFirestoreDoc<{
    state: IntegrationStatus;
  }>(`integrations/workspace`, {
    live: true,
    tenantAware: true,
  });

  const { doc: azureAdDoc, loading: azureAdLoading } =
    useFirestoreDoc<AzureAdIntegration>(`integrations/azure-ad`, {
      live: true,
      tenantAware: true,
    });

  const { doc: entraDoc, loading: entraLoading } =
    useFirestoreDoc<EntraIdIntegration>(`integrations/entra-id`, {
      live: true,
      tenantAware: true,
    });

  const { doc: azureDoc, loading: azureLoading } =
    useFirestoreDoc<AzureIntegration>(`integrations/azure`, {
      live: true,
      tenantAware: true,
    });

  const installed: Record<AssessmentScopeIntegration, boolean> = useMemo(
    () => ({
      aws: !!size(installedItems("iam-assessment", awsDoc?.data ?? {})),
      azure: azureAssessment
        ? !!size(installedItems("iam-assessment", azureDoc?.data ?? {}))
        : false,
      custom: !!customAssessment, // Controlled by LaunchDarkly feature flag
      gcloud: !!(
        size(installedItems("iam-assessment", gcloudDoc?.data ?? {})) ||
        size(installedItems("org-iam-assessment", gcloudDoc?.data ?? {}))
      ),
      k8s: false, //TODO: Update to make this reflect accurately
      okta:
        oktaAssessment || true
          ? !!size(installedItems("listing", oktaDoc?.data ?? {}))
          : false,
      workspace: workspaceDoc?.data?.state === "installed",
      "azure-ad": entraAssessment
        ? azureAdDoc?.data &&
          (isLegacyAzureAdIntegration(azureAdDoc.data)
            ? azureAdDoc.data.state === "installed"
            : !!size(installedItems("iam-write", azureAdDoc.data)))
        : false,
      "entra-id": entraAssessment
        ? !!size(installedItems("iam-write", entraDoc?.data ?? {}))
        : false,
    }),
    [
      awsDoc?.data,
      azureAssessment,
      azureDoc?.data,
      customAssessment,
      gcloudDoc?.data,
      oktaAssessment,
      oktaDoc?.data,
      workspaceDoc?.data?.state,
      entraAssessment,
      azureAdDoc?.data,
      entraDoc?.data,
    ]
  );

  const loading =
    awsLoading ||
    gcloudLoading ||
    oktaLoading ||
    azureAdLoading ||
    azureLoading ||
    entraLoading ||
    workspaceLoading;

  return { loading, installed };
};
